import React, { useCallback, useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import WpButton from 'Wpds/components/button/WpButton';

import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';
import { useQueryParam } from 'Shared/utils/hooks';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

import tadaEmojiSrc from './tada-emoji.png';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useOrganization } from 'Shared/utils/hooks/use-organization';

// Components
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './InviteTeammatesButton.scss';

type Props = {
  onCloseNavbar: Function;
};

function InviteTeammatesButton({ onCloseNavbar }: Props) {
  const isExpanded = useContext(ExpandNavbarContext);
  const { simpleInviteModal } = useFlags();
  const { isThirdPartyUser } = useCurrentUser();
  const { isPiDemoAccount } = useOrganization();

  const [, setMultipleInviteParam] = useQueryParam(QueryParamConstants.MULTIPLE_INVITE_MODAL);
  const [, setSingleInviteParam] = useQueryParam(QueryParamConstants.INVITE_BY_EMAIL_MODAL);

  const showModal = useCallback(() => {
    if (simpleInviteModal) setMultipleInviteParam(true);
    else setSingleInviteParam(true);
  }, [simpleInviteModal, setSingleInviteParam, setMultipleInviteParam]);

  const handleInviteTeammatesClick = useCallback(() => {
    (window as any).analytics.track('Clicked Add Workspace', { isGroup: false, isMultiple: true, source: 'sidebar' });
    showModal();
    if (onCloseNavbar) onCloseNavbar();
  }, [onCloseNavbar, showModal]);

  return (
    <div className="InviteTeammatesButton">
      {isExpanded ? (
        <WpButton
          variant="secondary"
          onClick={handleInviteTeammatesClick}
          size="lg"
          block
          disabled={isThirdPartyUser && !isPiDemoAccount}
          qaTest="sidebar-add-teammates-button"
        >
          Invite Teammates
          <span className="InviteTeammatesButton__emoji ml-2">
            <img width="17" height="17" src={tadaEmojiSrc} alt="tada-emoji" />
          </span>
        </WpButton>
      ) : (
        <WpTooltip placement="right">
          <WpTooltipTrigger>
            <WpButton
              size="sm"
              onClick={handleInviteTeammatesClick}
              icon
              disabled={isThirdPartyUser && !isPiDemoAccount}
              qaTest="sidebar-add-teammates-button"
            >
              <span className="InviteTeammatesButton__emoji">
                <img width="16" height="16" src={tadaEmojiSrc} alt="tada-emoji" />
              </span>
            </WpButton>
          </WpTooltipTrigger>
          <WpTooltipContent>Invite Teammates</WpTooltipContent>
        </WpTooltip>
      )}
    </div>
  );
}

export default InviteTeammatesButton;
